import { Badge, Button, Flex, Icon, Text } from '@chakra-ui/react';
import { getMarketCountByCategory } from '@features/prediction-markets/fakeMarketData';
import { TIME_CATEGORIES, TimeCategory } from '@features/prediction-markets/types';
import { ComponentType, memo, useCallback } from 'react';
import { BsBarChart } from 'react-icons/bs';
import { LuCalendarRange, LuLayoutGrid, LuTrendingUp } from 'react-icons/lu';

const CATEGORY_ICONS: Record<TimeCategory, ComponentType<{ size?: number }>> = {
  all: LuLayoutGrid,
  weekly: BsBarChart,
  monthly: LuTrendingUp,
  yearly: LuCalendarRange,
};

interface Props {
  selected: TimeCategory;
  onSelect: (category: TimeCategory) => void;
}

const TimeCategorySidebar = ({ selected, onSelect }: Props) => {
  return (
    <Flex direction="column" gap={1} minW="180px" w="180px" py={2} hideBelow="md">
      {TIME_CATEGORIES.map((cat) => (
        <CategoryItem
          key={cat.key}
          category={cat.key}
          label={cat.label}
          icon={CATEGORY_ICONS[cat.key]}
          count={getMarketCountByCategory(cat.key)}
          isSelected={selected === cat.key}
          onSelect={onSelect}
        />
      ))}
    </Flex>
  );
};

export const TimeCategoryTopBar = memo(({ selected, onSelect }: Props) => {
  return (
    <Flex hideFrom="md" gap={1} overflowX="auto" pb={2}>
      {TIME_CATEGORIES.map((cat) => {
        const isSelected = selected === cat.key;
        const CategoryIcon = CATEGORY_ICONS[cat.key];
        return (
          <TopBarItem
            key={cat.key}
            category={cat.key}
            label={cat.label}
            isSelected={isSelected}
            icon={CategoryIcon}
            onSelect={onSelect}
          />
        );
      })}
    </Flex>
  );
});

TimeCategoryTopBar.displayName = 'TimeCategoryTopBar';

interface TopBarItemProps {
  category: TimeCategory;
  label: string;
  isSelected: boolean;
  icon: ComponentType<{ size?: number }>;
  onSelect: (category: TimeCategory) => void;
}

const TopBarItem = memo(
  ({ category, label, isSelected, icon: IconComp, onSelect }: TopBarItemProps) => {
    const handleClick = useCallback(() => {
      onSelect(category);
    }, [category, onSelect]);

    return (
      <Flex
        as="button"
        direction="column"
        alignItems="center"
        justifyContent="center"
        gap={1.5}
        flex={1}
        minW="70px"
        py={2.5}
        px={3}
        borderRadius="xl"
        bg={isSelected ? 'surface.accent' : 'transparent'}
        _hover={{ bg: isSelected ? 'surface.accent' : 'surface.hover' }}
        cursor="pointer"
        onClick={handleClick}
      >
        <Icon color={isSelected ? 'text.primary' : 'text.secondary'} boxSize={5}>
          <IconComp />
        </Icon>
        <Text
          fontSize="xs"
          fontWeight={isSelected ? 'semibold' : 'medium'}
          color={isSelected ? 'text.primary' : 'text.secondary'}
          whiteSpace="nowrap"
        >
          {label}
        </Text>
      </Flex>
    );
  },
);

TopBarItem.displayName = 'TopBarItem';

interface CategoryItemProps {
  category: TimeCategory;
  label: string;
  icon: ComponentType<{ size?: number }>;
  count: number;
  isSelected: boolean;
  onSelect: (category: TimeCategory) => void;
}

const CategoryItem = memo(({ category, label, icon: IconComp, count, isSelected, onSelect }: CategoryItemProps) => {
  const handleClick = useCallback(() => {
    onSelect(category);
  }, [category, onSelect]);

  return (
    <Button
      variant="ghost"
      justifyContent="space-between"
      w="full"
      px={3}
      py={2}
      h="auto"
      borderRadius="lg"
      bg={isSelected ? 'surface.accent' : 'transparent'}
      _hover={{ bg: isSelected ? 'surface.accent' : 'surface.hover' }}
      onClick={handleClick}
    >
      <Flex alignItems="center" gap={2}>
        <Icon color={isSelected ? 'text.primary' : 'text.secondary'} boxSize={4}>
          <IconComp />
        </Icon>
        <Text fontSize="sm" fontWeight={isSelected ? 'semibold' : 'medium'} color="text.primary">
          {label}
        </Text>
      </Flex>
      <Badge
        variant="subtle"
        fontSize="xs"
        borderRadius="full"
        px={2}
        bg={isSelected ? 'surface.accentStrong' : 'surface.muted'}
        color="text.secondary"
      >
        {count}
      </Badge>
    </Button>
  );
});

CategoryItem.displayName = 'CategoryItem';

export default memo(TimeCategorySidebar);
